import { ChangeEvent, useEffect, useState } from 'react'
import QRCode from 'qrcode'

import { Button, Input } from '@/components/ui'
import FormGroup from '@/core/components/FormGroup'
import { QrScanningQuestConfigDto } from '@/common/models/gamification'

interface QrScanningConfigFieldsProps {
  value: QrScanningQuestConfigDto
  onChange: (next: QrScanningQuestConfigDto) => void
  /**
   * The server-provided deep link (`QuestDto.deepLink` / `QuestDraft.deepLink`), verbatim —
   * `/roadmap?qr_scan={questId}&code={scanCode}`. Only present once the quest has been saved (the
   * backend mints the `code` at that point). Never reconstruct this client-side: the `code` is a
   * server-generated secret the client cannot compute.
   */
  deepLink?: string | null
  /**
   * Quest id — used ONLY to name the downloaded QR file, so an admin printing signage for many
   * locations can tell the files apart. Deliberately not the `scanCode`: that is a secret and must
   * not end up in a download history or a synced Downloads folder. Never used to build the link.
   */
  questId?: string
}

/**
 * Editor for a QR_SCANNING quest's config: a location (latitude/longitude) and a validation radius
 * (metres) the client geofences against. Once the quest is saved, it also renders the
 * server-provided deep link and a downloadable QR code encoding it verbatim (for the admin to
 * print), plus the read-only scan code for support/debugging.
 */
export const QrScanningConfigFields = ({
  value,
  onChange,
  deepLink,
  questId,
}: QrScanningConfigFieldsProps) => {
  const latInvalid =
    value.latitude === undefined || value.latitude < -90 || value.latitude > 90
  const lngInvalid =
    value.longitude === undefined ||
    value.longitude < -180 ||
    value.longitude > 180
  const radiusInvalid =
    value.validationRadius === undefined || value.validationRadius <= 0

  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!deepLink) {
      setQrDataUrl(null)
      return
    }
    let cancelled = false
    QRCode.toDataURL(deepLink, { width: 240, margin: 1 })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url)
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null)
      })
    return () => {
      cancelled = true
    }
  }, [deepLink])

  const numberOrUndefined = (e: ChangeEvent<HTMLInputElement>) =>
    e.target.value === '' ? undefined : Number(e.target.value)

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormGroup
          label="Latitude"
          error={latInvalid ? 'Must be between -90 and 90' : undefined}
        >
          <Input
            type="number"
            step="any"
            isInvalid={latInvalid}
            value={value.latitude ?? ''}
            onChange={(e) =>
              onChange({ ...value, latitude: numberOrUndefined(e) })
            }
          />
        </FormGroup>
        <FormGroup
          label="Longitude"
          error={lngInvalid ? 'Must be between -180 and 180' : undefined}
        >
          <Input
            type="number"
            step="any"
            isInvalid={lngInvalid}
            value={value.longitude ?? ''}
            onChange={(e) =>
              onChange({ ...value, longitude: numberOrUndefined(e) })
            }
          />
        </FormGroup>
      </div>

      <FormGroup
        label="Validation radius (metres)"
        error={radiusInvalid ? 'Must be greater than 0' : undefined}
      >
        <Input
          type="number"
          step="any"
          min="0"
          isInvalid={radiusInvalid}
          value={value.validationRadius ?? ''}
          onChange={(e) =>
            onChange({ ...value, validationRadius: numberOrUndefined(e) })
          }
        />
      </FormGroup>

      {deepLink ? (
        <div className="space-y-3 rounded-lg border border-monochrome-120 p-4">
          <div className="text-body-medium text-monochrome-1">QR deep link</div>
          <div className="flex items-center gap-2">
            <Input readOnly value={deepLink} />
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigator.clipboard?.writeText(deepLink)}
            >
              Copy
            </Button>
          </div>

          <FormGroup label="Scan code">
            <Input readOnly value={value.scanCode ?? ''} />
          </FormGroup>
          <div className="text-xs text-monochrome-2">
            Generated automatically by the server; cannot be edited here.
          </div>

          {qrDataUrl && (
            <div className="flex items-center gap-4">
              <img src={qrDataUrl} alt="QR code" width={160} height={160} />
              <a
                href={qrDataUrl}
                download={`qr-${questId ?? 'quest'}.png`}
                className="text-accent-1 underline"
              >
                Download QR
              </a>
            </div>
          )}
        </div>
      ) : (
        <div className="text-xs text-monochrome-2">
          Save the quest to generate its QR code.
        </div>
      )}
    </div>
  )
}
